import React, { useState } from 'react';
import PublicRoute from 'enums/publicRoutes';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useIntl } from 'react-intl';
import { Button, DatePicker, Input, Table, Dropdown, Modal } from 'ui';
import { DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Filter from '../../../components/Filter';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import EnumSelect from '../../../Selects/EnumSelect';
import { Pagination } from '../../../../types/Pagination';
import { Tabs } from 'antd';
import {
  OrderStatus,
  ActionsKeys,
  OrderPaymentStatus,
} from '../../../../enums/order-status';
import { OrdersViewOrder } from '../OrdersViewOrder/OrdersViewOrder';
import { Transaction } from '../../../../interfaces/BackendModels';
import { StyledOrderListPage } from './style';
import { OrderPaymentStatusEnum } from '../../../../config/config';

export type OrderPaymentStatusCode = keyof typeof OrderPaymentStatusEnum &
  number;

const OrderListPage = () => {
  const [advancedSearch, setAdvancedSearch] = useState({});
  const [paginationStatus, setPaginationStatus] = useState<Pagination>({});
  const [reloadPage, setReloadPage] = useState<number>(0);
  const [confirmOrderId, setConfirmOrderId] = useState<number>(0);
  const [cancelOrderId, setCancelOrderId] = useState<number>(0);
  const [isOnStatusChange, setIsOnStatusChange] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [isOpenViewOrderDrawer, setIsOpenViewOrderDrawer] =
    useState<boolean>(false);
  const intl = useIntl();

  const { appendData: changeStatus } = useQueryApiClient({
    request: {
      url: `api/v2/transactions/change-status`,
      method: 'POST',
    },
    onSuccess: () => {
      setIsOnStatusChange(false);
      setReloadPage(reloadPage + 1);
    },
    onError: () => {
      setIsOnStatusChange(false);
    },
  });

  const onCloseDrawer = () => {
    setSelectedTransaction(null);
    setIsOpenViewOrderDrawer(false);
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'general.date' }),
      dataIndex: 'created_at',
      render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: intl.formatMessage({ id: 'orders.customer' }),
      dataIndex: 'customer_name',
    },
    {
      title: intl.formatMessage({ id: 'orders.number' }),
      dataIndex: 'number',
    },
    {
      title: intl.formatMessage({ id: 'orders.status' }),
      dataIndex: 'status',
      render: (value: string) => {
        if (value) {
          return intl.formatMessage({ id: `enum.${value.toUpperCase()}` });
        } else {
          return intl.formatMessage({ id: 'enum.PROCCESS' });
        }
      },
    },
    {
      title: intl.formatMessage({ id: 'orders.payment_status' }),
      dataIndex: 'payment_status',
      render: (value: OrderPaymentStatusCode): string => {
        return intl.formatMessage({
          id: `orders.${OrderPaymentStatusEnum[value] ?? 'failed'}`,
        });
      },
    },
    {
      title: intl.formatMessage({ id: 'orders.total' }),
      dataIndex: 'total',
      render: (value: string) => (
        <span className="table-currency">€ {parseFloat(value).toFixed(2)}</span>
      ),
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      render: (record: any) => {
        return (
          <Dropdown
            className="actions"
            overlayClassName="status-actions"
            menu={{
              items: [
                {
                  label: intl.formatMessage({ id: 'general.buyer_order' }),
                  key: 'buyer_order',
                },
                {
                  label: intl.formatMessage({ id: 'general.view' }),
                  key: 'view',
                },
                ...(record.status !== OrderStatus.CANCELED &&
                Object.values(OrderPaymentStatus).includes(
                  record.payment_status
                )
                  ? [
                      {
                        label: intl.formatMessage({ id: 'general.confirm' }),
                        key: '1',
                      },
                      {
                        label: intl.formatMessage({ id: 'general.canceled' }),
                        key: '2',
                      },
                    ]
                  : []),
              ],
              onClick: (value) => {
                if (value.key === 'buyer_order') {
                  window.open(
                    `${PublicRoute.USER_ORDER}?order_id=${record.id}&date=${dayjs(
                      record.created_at
                    ).format('DD.MM.YYYY')}`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }

                if (value.key === ActionsKeys.CONFIRM) {
                  setConfirmOrderId(record.id);
                  return;
                }

                if (value.key === ActionsKeys.CANCEL) {
                  setCancelOrderId(record.id);
                  return;
                }

                if (value.key === 'view') {
                  setIsOpenViewOrderDrawer(true);
                  setSelectedTransaction(record);
                }
              },
            }}
          >
            <Button
              className="action"
              label={intl.formatMessage({ id: 'general.actions' })}
              icon={<DownOutlined />}
              iconPosition="end"
              size="middle"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <DefaultLayout.PageLayout
      breadcrumbs={[
        {
          title: intl.formatMessage({ id: 'navigation.beginning' }),
        },
        {
          title: intl.formatMessage({ id: 'navigation.orders' }),
        },
      ]}
    >
      <Modal
        open={confirmOrderId > 0}
        onCancel={() => setConfirmOrderId(0)}
        destroyOnClose
        footer={[
          <Button
            key="yes"
            type="primary"
            onClick={() => {
              setIsOnStatusChange(true);
              changeStatus({
                status: OrderPaymentStatus.CONFIRM,
                id: confirmOrderId,
              });
              setConfirmOrderId(0);
            }}
            label={intl.formatMessage({ id: 'general.yes' })}
          />,
          <Button
            key="no"
            type="primary"
            onClick={() => {
              setConfirmOrderId(0);
            }}
            label={intl.formatMessage({ id: 'general.no' })}
          />,
        ]}
      >
        <h3>{intl.formatMessage({ id: 'general.confirm_purchase_info' })}</h3>
      </Modal>

      <Modal
        open={cancelOrderId > 0}
        onCancel={() => setCancelOrderId(0)}
        destroyOnClose
        footer={[
          <Button
            key="yes"
            type="primary"
            onClick={() => {
              setIsOnStatusChange(true);
              changeStatus({
                status: OrderPaymentStatus.CANCELED,
                id: cancelOrderId,
              });
              setCancelOrderId(0);
            }}
            label={intl.formatMessage({ id: 'general.yes' })}
          />,
          <Button
            key="no"
            type="primary"
            onClick={() => {
              setCancelOrderId(0);
            }}
            label={intl.formatMessage({ id: 'general.no' })}
          />,
        ]}
      >
        <h3>{intl.formatMessage({ id: 'general.cancel_purchase_info' })}</h3>
        <p>{intl.formatMessage({ id: 'general.transactions_warning' })}</p>
      </Modal>
      <Tabs
        items={[
          {
            key: 'purchases',
            label: intl.formatMessage({ id: 'navigation.purchases' }),
            children: (
              <DefaultLayout.PageContent>
                <Filter
                  onFinish={setAdvancedSearch}
                  totalRecord={paginationStatus?.total ?? 0}
                >
                  <DatePicker
                    label={intl.formatMessage({ id: 'general.date_from' })}
                    name="date_from"
                  />
                  <DatePicker
                    label={intl.formatMessage({ id: 'general.date_to' })}
                    name="date_to"
                  />
                  <span className="order-status-field">
                    <EnumSelect
                      label={intl.formatMessage({
                        id: 'orders.order_status',
                      })}
                      name="status"
                      code="OrderStatusType"
                    />
                  </span>
                  <Input
                    label={intl.formatMessage({ id: 'general.search' })}
                    name="search"
                  />
                  <Button
                    type="primary"
                    label={intl.formatMessage({ id: 'general.select' })}
                    htmlType="submit"
                  />
                </Filter>
                <StyledOrderListPage>
                  <Table
                    url="/api/v2/orders"
                    columns={columns}
                    filter={advancedSearch}
                    reload={reloadPage}
                    paginnationStatus={(el) => setPaginationStatus(el)}
                    loading={isOnStatusChange}
                    scroll={{ x: 'max-content' }}
                  />
                </StyledOrderListPage>
                <OrdersViewOrder
                  open={isOpenViewOrderDrawer}
                  onClose={onCloseDrawer}
                  transaction={selectedTransaction}
                />
              </DefaultLayout.PageContent>
            ),
          },
        ]}
      ></Tabs>
    </DefaultLayout.PageLayout>
  );
};

export default OrderListPage;
